import type { City, DoctorService } from "@hermes/api";
import { Temporal, useNow, useTz } from "@hermes/intl";

/**
 * Parses a nearest_slot_datetime into Temporal.PlainDate.
 * Converted with provided city's timezone in mind
 */
export const useDoctorServiceNearestDate = ({
  doctorService,
  city,
}: {
  doctorService?: Pick<DoctorService, "nearest_slot_datetime">;
  city?: City;
}) => {
  const tzFallback = useTz();

  return doctorService?.nearest_slot_datetime
    ? Temporal.Instant.from(doctorService.nearest_slot_datetime)
        .toZonedDateTimeISO(city?.timezone ?? tzFallback)
        .toPlainDate()
    : undefined;
};

/**
 * Returns a timestamp in the format of Temporal.PlainDateTime that indicates
 * a point in time from which an appointment is impossible, this is now + minutes_before_appointment
 *
 * `now` is resolved from timezone of a provided city, fallbacks to a timezone provided by useTz
 */
export const useDateTimeBeforeAppointment = ({
  doctorService,
  city,
}: {
  doctorService?: Pick<DoctorService, "minutes_before_appointment">;
  city?: City;
}) => {
  const tzFallback = useTz();
  const now = useNow("minute");

  return doctorService?.minutes_before_appointment
    ? now
        .withTimeZone(city?.timezone ?? tzFallback)
        .add({ minutes: doctorService.minutes_before_appointment })
        .toPlainDateTime()
    : undefined;
};
